/*
 * A choice made in a sheet runs once the sheet has gone and handed the focus back, not when it was
 * chosen. A dialog stays mounted for the length of its exit animation with its focus trap armed, and
 * gives the focus back to whatever opened it as it goes; an action that opened the editor, a dialog,
 * a picker or a field any earlier than that would be given the focus and have it taken away again —
 * which a popover reads as the focus leaving it and answers by dismissing itself, and a title field
 * answers by losing the caret it was just given. Waiting for that move puts things in the only order
 * that works: the sheet goes, its opener takes the focus back, and then what the reader asked for
 * opens and keeps it. Every sheet of menu entries (a message's actions, a document's name sheet)
 * chooses through this one hook.
 */

import { useCallback, useEffect, useRef, type ReactNode } from "react"

/**
 * Runs `go` once the sheet's own hand-over has happened, and not before.
 *
 * `ui/Dialog` gives the focus back to whatever opened the sheet, and it does so when its panel
 * finally leaves the document — on no clock a caller can predict: measured at 135 ms after the
 * unmount on a phone-sized window, behind the emoji vocabulary's own parse. So this waits for the
 * move rather than for a duration: the first focus that lands anywhere after the sheet has gone is
 * that hand-over, and the choice runs on the frame after it.
 *
 * The hand-over always comes — the dialog focuses the control that opened it, or the main region
 * when that control is gone — which is the contract `ui/Dialog` states and this depends on.
 */
function whenFocusSettles(go: () => void): void {
	const done = () => {
		document.removeEventListener("focusin", done, true)
		requestAnimationFrame(go)
	}
	document.addEventListener("focusin", done, true)
}

/**
 * The sheet's own going, reported. It is a child of the sheet's body rather than an effect of the
 * sheet itself, because the body is what an exit animation would keep mounted after the close: this
 * unmounts exactly when the panel stops being in anybody's way, whether the caller drops the whole
 * sheet at once or leaves it standing closed.
 */
function WhenGone({ run }: { run: () => void }) {
	useEffect(() => run, [run])
	return null
}

/**
 * `choose(run)` closes the sheet through `onOpenChange(false)` and runs `run` after the hand-over;
 * `gone` is the element the sheet's body draws so that the close is heard.
 */
export function useChoiceAfterClose(onOpenChange: (open: boolean) => void): {
	choose: (run: () => void) => void
	gone: ReactNode
} {
	const chosen = useRef<(() => void) | undefined>(undefined)
	const runChosen = useCallback(() => {
		const go = chosen.current
		chosen.current = undefined
		if (go === undefined) return
		whenFocusSettles(go)
	}, [])
	const choose = (run: () => void) => {
		chosen.current = run
		onOpenChange(false)
	}
	return { choose, gone: <WhenGone run={runChosen} /> }
}
